<template>
	<core-layout-content large>
		<core-layout-headline-and-actions>
			<template slot="headline">
				<div class="flex xs-middle">
					<!-- Back button -->
					<j-button
						size="small"
						type="secondary"
						@click="$router.push({ name: 'TemplatesList' })"
						class="mr2"
					>
						<crm-icon name="back" size="normal" color="c5" class="mr1" />
					</j-button>
					{{ computedTemplateName }}
				</div>
			</template>
			<template slot="actions">
				<!-- Download Preview -->
				<j-button size="small" @click="$modal.show('preview_template')">
					<crm-icon name="file-download" size="small" color="c5" class="mr1" />
					{{ $t("download_preview") }}
				</j-button>
			</template>
		</core-layout-headline-and-actions>

		<!-- Tabs -->
		<j-tabs
			:selectedTab="selectedTab"
			:tabs="[
				{
					name: $t('editor'),
					value: 'editor'
				},
				{
					name: $t('settings'),
					value: 'settings'
				}
			]"
			@click="selectedTab = $event"
		/>

		<!-- Editor -->
		<j-card
			class="bg5"
			v-if="selectedTab === 'editor' && templateType === 'pdf_drawing'"
		>
			<!-- if isLoading -->
			<div v-if="isLoading"></div>
			<!-- if error on PDF -->
			<div v-else-if="!isLoading && error">{{ $t("error_missing_pdf") }}</div>
			<pdf-drawing-template
				v-else-if="!isLoading && !error"
				:drawings="drawings"
				:source="source"
				:templateId="id"
				@updateDrawing="updateDrawing"
				@savePdfDrawing="savePdfDrawing"
				@addPdfDrawing="addPdfDrawing"
				@removePdfDrawing="removePdfDrawing"
				@removeAll="removeAll"
				@error="handleError"
				@updated:pdfDrawing="fetch"
				@removed:pdfDrawing="removePdfDrawing"
			/>
		</j-card>

		<j-card
			class="bg5"
			v-if="selectedTab === 'editor' && templateType === 'wkhtml_to_pdf'"
		>
			<wkhtmltopdf-template
				:id="template.id"
				:payload="computedHtmlPayload"
				@update="fetch"
				@updateHtml="updateHtml"
			/>
		</j-card>

		<!-- Settings -->
		<j-card class="bg5" v-if="selectedTab === 'settings'">
			<pdf-template-settings
				:templateType="templateType"
				:payload="template"
				@update="fetch"
			/>
		</j-card>

		<!-- Bottom Actions -->
		<div class="w-full flex justify-end">
			<div
				class="flex mt6 mr2"
				v-if="selectedTab === 'editor' && templateType === 'wkhtml_to_pdf'"
			>
				<!-- Save -->
				<j-button
					type="primary"
					size="small"
					@click="updateWkhtmltopdfTemplate"
				>
					<crm-icon name="add" size="normal" color="c5" class="mr1" />
					{{ $t("save") }}
				</j-button>
			</div>

			<div class="flex mt6">
				<!-- Delete -->
				<j-button type="error" size="small" @click="$modal.show('j-dialog')">
					<crm-icon name="delete" size="normal" color="c5" class="mr1" />
					{{ $t("delete_template") }}
				</j-button>
			</div>
		</div>

		<!-- Delete Modal/Dialog -->
		<j-dialog
			:title="$t('delete_template')"
			type="delete"
			@confirm="deleteTemplate"
			>{{ $t("errors.are_you_sure_you_want_to_delete_template") }}</j-dialog
		>

		<!-- Preview Modal -->
		<core-modal name="preview_template" :headline="$t('preview_template')">
			<div class="flex wrap p4">
				<core-graphql-variables-builder
					:graphQlQuery="graphQlQuery"
					@update:variables="graphQlVariables = $event"
				/>
				<div class="flex xs-12 mt3 xs-right">
					<j-button
						size="small"
						@click="downloadPreviewFile"
						:isLoading="isLoading"
						>{{ $t("download_preview") }}</j-button
					>
				</div>
			</div>
		</core-modal>
	</core-layout-content>
</template>

<script>
import CrmIcon from "@/components/CrmIcon"
import CoreLayoutContent from "@/modules/core/components/CoreLayoutContent.vue"
import CoreLayoutHeadlineAndActions from "@/modules/core/components/CoreLayoutHeadlineAndActions.vue"
import CoreFormInput from "@/modules/core/components/CoreFormInput"
import PdfDrawingTemplate from "../components/PdfDrawingTemplate/"
import WkhtmltopdfTemplate from "../components/WkhtmltopdfTemplate/"
import PdfTemplateSettings from "../components/PdfTemplateSettings.vue"
import JButton from "@/components/JButton"
import JTabs from "@/components/JTabs"
import JCard from "@/components/JCard"
import JDialog from "@/components/JDialog/"
import CoreModal from "@/modules/core/components/CoreModal"
import CoreGraphqlVariablesBuilder from "@/modules/core/components/CoreGraphqlVariablesBuilder/"
import debounce from "lodash.debounce"
import api from "../api/"

const fabricToDrawing = ({
	point1,
	point2,
	liquidSource,
	page,
	font,
	verticalAlignment = "CENTER",
	textAlignment = "LEFT"
}) => {
	return {
		point1,
		point2,
		liquidSource,
		page,
		font,
		verticalAlignment,
		textAlignment
	}
}

const sortDrawings = (drawings, sortBy = "desc") => {
	const sortFn =
		sortBy === "desc"
			? (a, b) => a?.page - b?.page
			: (a, b) => a?.page + b?.page

	return drawings.sort(sortFn)
}

export default {
	components: {
		CrmIcon,
		CoreLayoutContent,
		CoreLayoutHeadlineAndActions,
		PdfDrawingTemplate,
		WkhtmltopdfTemplate,
		PdfTemplateSettings,
		JButton,
		JTabs,
		JCard,
		JDialog,
		CoreFormInput,
		CoreModal,
		CoreGraphqlVariablesBuilder
	},
	props: {
		id: {
			type: String
		}
	},

	data: () => ({
		source: null,
		isLoading: false,
		updating: false,
		drawings: null,
		error: null,
		template: null,
		fileName: null,
		graphQlQuery: "",
		graphQlVariables: null,
		description: null,
		selectedTab: "editor",
		templateType: null,
		bodyHtml: null,
		headerHtml: null,
		footerHtml: null
	}),
	computed: {
		computedTemplateName() {
			return this.template?.name
		},
		computedHtmlPayload() {
			return {
				bodyHtml: this.template.html,
				headerSettings: this.template.headerSettings,
				footerSettings: this.template.footerSettings
			}
		}
	},
	methods: {
		updateHtml(value) {
			if (value.type === "header") {
				this.headerHtml = value.value
			} else if (value.type === "body") {
				this.bodyHtml = value.value
			} else {
				this.footerHtml = value.value
			}
		},
		async updateWkhtmltopdfTemplate() {
			if (!this.headerHtml)
				this.headerHtml = this.computedHtmlPayload.headerSettings.html
			if (!this.bodyHtml) this.bodyHtml = this.computedHtmlPayload.bodyHtml
			if (!this.footerHtml)
				this.footerHtml = this.computedHtmlPayload.footerSettings.html
			const res = await api.updateWkhtmltopdfTemplate({
				templateId: this.template.id,
				input: {
					html: this.bodyHtml,
					headerSettings: {
						html: `<!DOCTYPE html>${this.headerHtml}`,
						spacing: this.computedHtmlPayload.headerSettings.spacing
					},
					footerSettings: {
						html: this.footerHtml,
						spacing: this.computedHtmlPayload.footerSettings.spacing
					}
				}
			})
			if (res.data.updateWkhtmltopdfTemplate.errors) {
				this.$store.dispatch(
					"showError",
					res.data.updateWkhtmltopdfTemplate.errors
				)
			} else {
				this.$store.dispatch("addMessage", {
					message: {
						type: "success",
						text: this.$t("template_updated")
					}
				})
			}
			await this.fetch()
		},
		setFileName(value) {
			this.fileName = value
		},

		async updateTemplate() {
			if (!this.fileName) return

			let apiKey =
				this.templateType === "pdf_drawing"
					? "updatePdfDrawingTemplate"
					: "updateWkhtmltopdfTemplate"

			const response = await api[apiKey]({
				templateId: this.id,
				input: {
					name: this.fileName,
					description: this.description,
					graphQlQuery: this.graphQlQuery
				}
			})

			this.template = await this.fetch()

			this.$store.dispatch("addMessage", {
				message: {
					type: "success",
					text: this.$t("template_updated")
				}
			})
		},

		navigateTo() {
			this.$router.push({ name: "TemplatesList" })
		},

		async addPdfDrawing(input) {
			if (!input) return

			const response = await api.createPdfDrawing({
				templateId: this.id,
				input: fabricToDrawing(input)
			})

			await this.fetch()
			this.drawings = sortDrawings(this.template.drawings)
		},

		async removePdfDrawing(drawingId) {
			if (!drawingId) return

			const response = await api.removePdfDrawing({
				templateId: this.id,
				drawingId: drawingId
			})

			await this.fetch()
			this.drawings = sortDrawings(this.template.drawings)
		},

		async updateDrawing(input) {
			const index = this.drawings.findIndex(d => d.id === input.id)
			this.drawings.splice(index, 1, input)
		},

		async savePdfDrawing(input) {
			if (!input) return

			if (this.updating) return

			if (!this.updating) {
				this.updating = true
				const response = await api.updatePdfDrawing({
					templateId: this.id,
					drawingId: input.id,
					input: fabricToDrawing(input)
				})
				this.updating = false
			}

			await this.fetch()
			this.drawings = sortDrawings(this.template.drawings)
		},

		async removeAll() {
			await Promise.all(
				this.drawings.map(async drawing => {
					await api.removePdfDrawing({
						templateId: this.id,
						drawingId: drawing.id
					})
				})
			)

			await this.fetch()
			this.drawings = sortDrawings(this.template.drawings)
		},

		async fetch() {
			const response = await api.templateWithDrawings({
				where: { id: this.id },
				limit: 1
			})

			this.template = response?.data?.templates?.list?.find(
				template => template.id === this.id
			)

			// for drawings
			if (this.template.hasOwnProperty("pdfUrl")) {
				this.templateType = "pdf_drawing"
				this.drawings = sortDrawings(this.template.drawings)
				this.source = `https://api.asia.covergo.com/api/v1/files/${encodeURIComponent(
					this.template.pdfUrl
				)}`
			}

			// for wkhtmltopdf
			if (this.template.hasOwnProperty("html")) {
				this.templateType = "wkhtml_to_pdf"
			}

			// for both drawings and wkhtmltopdf
			this.fileName = this.computedTemplateName
			this.graphQlQuery = this.template.graphQlQuery
				? this.template.graphQlQuery
				: ""
			this.description = this.template.description
		},

		handleError(error) {
			this.error = error.message

			this.$store.dispatch("addMessage", {
				message: { type: "error", text: this.error }
			})
		},

		async deleteTemplate() {
			const res = await api.deleteTemplate({
				templateId: this.$route.params.id
			})

			if (res.errors) this.$store.dispatch("showError", res.errors)
			else
				this.$store.dispatch("addMessage", {
					message: {
						type: "success",
						text: this.$t("template_deleted")
					}
				})
			this.$router.push({ name: "TemplatesList" })
		},

		onGraphQlQueryChange: debounce(function() {
			this.updateTemplate()
		}, 500),

		async downloadPreviewFile() {
			let apiKey =
				this.templateType === "pdf_drawing"
					? "renderPdfDrawingTemplate"
					: "renderWkhtmltopdfTemplate"

			this.isLoading = true

			const res = await api[apiKey]({
				templateId: this.template.id,
				input: {
					name: "data",
					variablesJsonString: this.graphQlVariables
						? this.graphQlVariables
						: null
				}
			})

			this.isLoading = false

			if (res.data?.[apiKey]?.errors && res.data?.[apiKey]?.errors?.length) {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text: res?.errors?.toString() || res.data?.[apiKey]?.errors
					}
				})
			}

			console.log("res.errors.length", res)

			if (res.data?.[apiKey]?.status === "success") {
				let bytes = new Uint8Array(res.data?.[apiKey]?.value) // pass your byte response to this constructor
				let blob = new Blob([bytes], { type: "application/pdf" }) // change resultByte to bytes
				let link = document.createElement("a")
				link.href = window.URL.createObjectURL(blob)
				link.download = "test.pdf"
				link.click()
			}

			this.$modal.hide("preview_template")
		}
	},

	async created() {
		this.isLoading = true
		await this.fetch()
		this.isLoading = false
	}
}
</script>
<style scoped>
.justify-end {
	justify-content: flex-end;
}
</style>
